/** @jsxImportSource theme-ui */
import { Text } from 'theme-ui'

import Link from 'next/link'
import React, {
  ComponentPropsWithoutRef,
  CSSProperties,
  useMemo,
  useRef,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Box, Button, Container, Flex } from 'theme-ui'
import { Icon } from '@iconify/react'

// import { useResponsiveValue } from '@theme-ui/match-media'
import { ShopName } from 'src/constants'
import { useTheme } from 'src/hooks/use-theme'
import { useWhyDidYouUpdate } from 'src/hooks/use-why-did-you-update'
import { Styled } from '~/components/styled'
import { ContentfulRichText } from '../Contentful-Rich-Text'
import { AnimatedGradientTextDemo } from '~/components/AnimatedGradientText'
import { TextGenerateEffect } from '~/components/ui/text-generate-effect'
import { ButtonEntranceEffect } from '~/components/ui/button-entrance-effect'

type Props = {
  preTitle?: string
  title: string | null
  titleTag?: string | null
  titleSize?: 'normal' | 'big' | 'huge'
  description?: any
  buttons?: {
    text: string
    to?: string | null
    variant?: string | null
  }[]
  verticalPosition?:
    | ('top' | 'center' | 'bottom')[]
    | ('top' | 'center' | 'bottom')
  horizontalPosition?: 'left' | 'center' | 'right'
  textAlign?: 'left' | 'center' | 'right'
  /**
   * Array that matches up to theme for height
   */
  height?: (string | number | 'full-screen')[]
  /**
   * Array that matches up to theme for maxHeight
   */
  maxHeight?: (string | number | 'full-screen')[]
  /**
   * Array that matches up to theme for minHeight
   */
  minHeight?: (string | number | 'full-screen' | null)[]
  /**
   * Array that matches up to theme for text color
   */
  textColor?: (string | number | null)[]
  /**
   * Array that matches up to theme for content width on tablet and greater
   */
  contentMaxWidth?: (string | number | null)[]
  /**
   * Where should text be positioned relative to the video on mobile screens.
   * Can be `cover video` or `under video`.
   *
   * Default: `under video`
   */
  contentMobilePosition?: 'cover video' | 'under video'
  contentPadding?: string[]
  titleLineHeight?: string[]
  video: {
    url: string
    webmUrl?: string
    avifUrl?: string
    width?: number
    height?: number
  }
  secondButtonText?: string
  secondButtonUrl?: string
  additionalButtonText?: string
  additionalButtonUrl?: string
  forthButtonText?: string
  forthButtonUrl?: string
  // Video specific props
  autoplay?: boolean
  muted?: boolean
  loop?: boolean
  controls?: boolean
  poster?: string
  // Performance props
  lazyLoad?: boolean
  preload?: 'none' | 'metadata' | 'auto'
  priority?: boolean
  quality?: 'low' | 'medium' | 'high'
  // SEO props
  videoSchema?: {
    name?: string
    description?: string
    thumbnailUrl?: string
    duration?: string
    uploadDate?: string
  }
}

const defaultProps: Partial<Props> = {
  minHeight: ['100vh'],
  textColor: [null, 'background'],
  contentMaxWidth: [null, '70%'],
  verticalPosition: ['center'],
  autoplay: false, // Mudamos para false por padrão para melhor performance
  muted: true,
  loop: true,
  controls: false,
  lazyLoad: true,
  preload: 'none',
  priority: false,
  quality: 'medium',
}

// Hook personalizado para lazy loading de vídeos
const useVideoLazyLoading = (options: {
  enabled: boolean
  threshold?: number
  rootMargin?: string
}) => {
  const [shouldLoad, setShouldLoad] = useState(!options.enabled)
  const [isInView, setIsInView] = useState(false)
  const ref = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (!options.enabled || shouldLoad) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry && entry.isIntersecting) {
          setIsInView(true)
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || '50px',
      }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [options.enabled, options.threshold, options.rootMargin, shouldLoad])

  return { ref, shouldLoad, isInView }
}

// Hook para performance de vídeo
const useVideoPerformance = (videoRef: React.RefObject<HTMLVideoElement>) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [wasPausedByScroll, setWasPausedByScroll] = useState(false)

  const handlePlay = useCallback(() => setIsPlaying(true), [])
  const handlePause = useCallback(() => setIsPlaying(false), [])
  const handleError = useCallback(() => setHasError(true), [])

  // Pausa/retoma vídeo baseado na visibilidade para economizar recursos
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry && !entry.isIntersecting && isPlaying) {
          // Vídeo saiu da tela e estava tocando - pausa e marca como pausado pelo scroll
          video.pause()
          setWasPausedByScroll(true)
        } else if (entry && entry.isIntersecting && wasPausedByScroll) {
          // Vídeo voltou a ficar visível e foi pausado pelo scroll - retoma
          video.play().catch(() => {
            // Se não conseguir tocar (ex: política de autoplay), não faz nada
            console.log('Não foi possível retomar o vídeo automaticamente')
          })
          setWasPausedByScroll(false)
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(video)

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('error', handleError)

    return () => {
      observer.disconnect()
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('error', handleError)
    }
  }, [
    videoRef,
    isPlaying,
    wasPausedByScroll,
    handlePlay,
    handlePause,
    handleError,
  ])

  return { isPlaying, hasError }
}

// Função para detectar suporte a formato de vídeo
const getOptimalVideoFormat = (video: Props['video']) => {
  const canPlayWebM =
    typeof document !== 'undefined' &&
    document.createElement('video').canPlayType('video/webm') !== ''

  if (canPlayWebM && video.webmUrl) {
    return { url: video.webmUrl, type: 'video/webm' }
  }

  return { url: video.url, type: 'video/mp4' }
}

// Função auxiliar para extrair texto do Contentful Rich Text
const extractTextFromRichText = (richText: any): string => {
  if (!richText?.json?.content) return ''

  const extractFromContent = (content: any[]): string => {
    return content
      .map((node) => {
        if (node.nodeType === 'text') {
          return node.value || ''
        } else if (node.content) {
          return extractFromContent(node.content)
        }
        return ''
      })
      .join(' ')
  }

  return extractFromContent(richText.json.content)
}

const VideoWithTextOverlay = (props: Props) => {
  const {
    verticalPosition: verticalPositionArray = defaultProps.verticalPosition,
    textAlign = 'center',
    title,
    titleTag = 'h2',
    buttons,
    additionalButtonText,
    additionalButtonUrl,
    forthButtonText,
    forthButtonUrl,
    description,
    minHeight: minHeightArray = defaultProps.minHeight,
    maxHeight,
    horizontalPosition,
    contentMaxWidth = defaultProps.contentMaxWidth,
    titleSize = 'normal',
    contentMobilePosition = 'under video',
    textColor = defaultProps.textColor,
    preTitle,
    contentPadding,
    titleLineHeight,
    video,
    autoplay = defaultProps.autoplay,
    muted = defaultProps.muted,
    loop = defaultProps.loop,
    controls = defaultProps.controls,
    poster,
    lazyLoad = defaultProps.lazyLoad,
    preload = defaultProps.preload,
    priority = defaultProps.priority,
    quality = defaultProps.quality,
    videoSchema,
  } = props

  // Hooks para otimização
  const { ref: videoRef, shouldLoad } = useVideoLazyLoading({
    enabled: Boolean(lazyLoad && !priority),
    threshold: 0.1,
    rootMargin: '100px',
  })

  const { isPlaying, hasError } = useVideoPerformance(videoRef)

  // Gera poster otimizado se não fornecido
  const optimizedPoster = useMemo(() => {
    if (poster) return poster
    if (
      videoSchema &&
      'thumbnailUrl' in videoSchema &&
      videoSchema.thumbnailUrl
    ) {
      return videoSchema.thumbnailUrl
    }
    // Fallback para uma captura de frame do vídeo
    return undefined
  }, [poster, videoSchema])

  // Gera structured data para SEO
  const structuredData = useMemo(() => {
    if (!videoSchema) return null

    const thumbnailUrl =
      'thumbnailUrl' in videoSchema ? videoSchema.thumbnailUrl : undefined

    return {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: videoSchema.name || title,
      description:
        videoSchema.description || extractTextFromRichText(description),
      thumbnailUrl: thumbnailUrl || optimizedPoster,
      uploadDate: videoSchema.uploadDate,
      duration: videoSchema.duration,
      contentUrl: video.url,
      embedUrl: video.url,
    }
  }, [videoSchema, title, description, optimizedPoster, video.url])

  // Detecta formato de vídeo otimizado
  const optimalFormat = useMemo(() => getOptimalVideoFormat(video), [video])

  const { fontSizes } = useTheme()
  const verticalPosition = Array.isArray(verticalPositionArray)
    ? verticalPositionArray
    : [verticalPositionArray]

  useWhyDidYouUpdate('[video-with-text-overlay]', props)

  let titleFontSize = titleSize === 'normal' ? [9] : [8, 9]
  if (titleSize === 'huge') titleFontSize = [7, 8, 9]

  let descriptionFontSize =
    titleSize === 'normal' ? ['1rem'] : ['1.25rem', '1.6875rem']
  if (titleSize === 'huge') descriptionFontSize = ['1.5rem', '1.6875rem']

  const minHeight = minHeightArray?.map(
    (h: string | number | 'full-screen' | null) =>
      h === 'full-screen' ? '100vh' : h
  )

  let justifyContent = ['center']
  if (horizontalPosition === 'left') {
    justifyContent = [...justifyContent, 'flex-start']
  } else if (horizontalPosition === 'right') {
    justifyContent = [...justifyContent, 'flex-end']
  }

  let buttonJustifyContent = textAlign === 'center' ? 'center' : 'flex-start'
  if (textAlign === 'right') buttonJustifyContent = 'flex-end'

  const maybeRenderContent = useMemo(() => {
    if (!title?.trim() && !description?.trim()) return null
    let HeaderTag: any = Styled.h2
    if (titleTag == 'h1') {
      HeaderTag = Styled.h1
    }

    return (
      <Flex
        sx={{
          alignItems: verticalPosition?.map((position) => {
            switch (position) {
              case 'bottom':
                return 'flex-end'
              case 'top':
                return 'flex-start'
              case 'center':
                return 'center'
              default:
                return 'center'
            }
          }),
          justifyContent: 'center',
          inset: 0,
          position: 'absolute',
          zIndex: 2, // Garante que o texto fique acima do overlay
          display: 'flex',
          flexDirection: 'column',
        }}
        pt={[
          4,
          verticalPosition?.[1] === 'top' || verticalPosition?.[0] === 'top'
            ? 3
            : null,
        ]}
        pb={[
          4,
          verticalPosition?.[1] === 'bottom' ||
          verticalPosition?.[0] === 'bottom'
            ? 4
            : null,
        ]}
        px={3}
      >
        <Container
          sx={{
            flex: 1,
            padding: contentPadding,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Flex
            sx={{
              justifyContent,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Box sx={{ width: contentMaxWidth }}>
              {/* Magic UI AnimatedGradientText */}
              {/* <Box sx={{ display: 'flex', justifyContent: textAlign, mb: 2 }}>
                <AnimatedGradientTextDemo
                  text="Free Shipping on orders over $75"
                  emoji="🎉"
                />
              </Box> */}

              {preTitle?.trim() && (
                <Text
                  sx={{
                    variant: 'text.preHeader',
                    textAlign,
                    fontSize: descriptionFontSize.map(
                      (font) => `calc(${fontSizes[font] ?? font} * 0.8)`
                    ),
                    mb: 1.5,
                  }}
                >
                  {preTitle}
                </Text>
              )}
              {!!title?.trim() && (
                <HeaderTag
                  sx={{
                    marginBottom: 1,
                    color:
                      (textColor?.filter(Boolean)[0] as string) || 'background',
                    textAlign,
                    filter: 'drop-shadow(0 0 15px rgba(0, 0, 0, 0.3))',
                    fontSize: [null, ...titleFontSize],
                    lineHeight: titleLineHeight,
                  }}
                >
                  <TextGenerateEffect words={title} duration={0.5} />
                </HeaderTag>
              )}
              {!!description && (
                <div
                  sx={{
                    textAlign,
                    filter: 'drop-shadow(0 0 15px rgba(0, 0, 0, 0.3))',
                    color:
                      (textColor?.filter(Boolean)[0] as string) || 'background',
                    fontSize: [null, ...descriptionFontSize],
                    width: '100%',
                  }}
                >
                  <TextGenerateEffect
                    words={extractTextFromRichText(description)}
                    duration={0.4}
                  />
                </div>
              )}
              {!!buttons?.length && (
                <Flex
                  marginTop={1.5}
                  sx={{
                    flexWrap: 'wrap',
                    justifyContent: buttonJustifyContent,
                    gap: [1, 4, 4],
                  }}
                >
                  {/* Primeiro botão - fica em cima */}
                  {buttons?.[0] && buttons[0].text && (
                    <ButtonEntranceEffect delay={800} duration={0.6}>
                      <Box
                        sx={{
                          paddingX: '5px',
                          paddingY: '5px',
                          borderRadius: '50px',
                          background: 'rgba(255, 255, 255, 0.3)',
                          backdropFilter: 'blur(100px)',
                          WebkitBackdropFilter: 'blur(100px)',
                          filter: 'drop-shadow(0 0 15px rgba(0, 0, 0, 0.3))',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          marginTop: 5,
                        }}
                      >
                        <Link
                          key={buttons[0].to || '#'}
                          href={buttons[0].to || '#'}
                          style={{ textDecoration: 'none' }}
                        >
                          <Button
                            variant={buttons[0].variant ?? 'pill'}
                            sx={{
                              background: 'white',
                              color: 'black',
                              border: 'none',
                              borderRadius: '50px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              gap: 2,
                              textDecoration: 'none',
                              position: 'relative',
                              overflow: 'hidden',
                              '& .button-icon': {
                                color: 'black',
                              },
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background:
                                  'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                                transition: 'left 0.5s ease',
                                zIndex: 1,
                              },
                              '&:hover': {
                                background:
                                  'linear-gradient(45deg, #11a3d8, #0d8bc7)',
                                color: 'white',
                                textDecoration: 'none',
                                '&::before': {
                                  left: '100%',
                                },
                                '& .button-icon': {
                                  color: 'white !important',
                                },
                                '& .button-text': {
                                  color: 'white',
                                },
                              },
                            }}
                          >
                            <Icon
                              icon="iconamoon:shopping-bag-fill"
                              className="button-icon"
                              style={{
                                fontSize: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                lineHeight: 1,
                                position: 'relative',
                                zIndex: 2,
                              }}
                            />
                            <Box
                              className="button-text"
                              sx={{
                                lineHeight: 1,
                                mt: 1,
                                py: 1,
                                position: 'relative',
                                zIndex: 2,
                              }}
                            >
                              {buttons[0].text}
                            </Box>
                          </Button>
                        </Link>
                      </Box>
                    </ButtonEntranceEffect>
                  )}

                  {/* Segundo botão - Dinâmico do Contentful */}
                  {buttons?.[1] && buttons[1].text && (
                    <ButtonEntranceEffect delay={900} duration={0.6}>
                      <Box
                        sx={{
                          paddingX: '5px',
                          paddingY: '5px',
                          borderRadius: '50px',
                          background: 'rgba(255, 255, 255, 0.3)',
                          backdropFilter: 'blur(100px)',
                          WebkitBackdropFilter: 'blur(100px)',
                          filter: 'drop-shadow(0 0 15px rgba(0, 0, 0, 0.3))',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          marginTop: 5,
                        }}
                      >
                        <Link
                          href={buttons[1].to || '#'}
                          style={{ textDecoration: 'none' }}
                        >
                          <Button
                            variant={buttons[1].variant ?? 'pill'}
                            sx={{
                              background: 'black',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              gap: 2,
                              textDecoration: 'none',
                              position: 'relative',
                              overflow: 'hidden',
                              '& .button-icon': {
                                color: 'white',
                              },
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background:
                                  'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                                transition: 'left 0.5s ease',
                                zIndex: 1,
                              },
                              '&:hover': {
                                background:
                                  'linear-gradient(45deg, #ff6b35, #f7931e)',
                                color: 'white',
                                textDecoration: 'none',
                                '&::before': {
                                  left: '100%',
                                },
                                '& .button-icon': {
                                  color: 'white !important',
                                },
                                '& .button-text': {
                                  color: 'white',
                                },
                              },
                            }}
                          >
                            <Icon
                              icon="mdi:tag-multiple"
                              className="button-icon"
                              style={{
                                fontSize: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                lineHeight: 1,
                                position: 'relative',
                                zIndex: 2,
                              }}
                            />
                            <Box
                              className="button-text"
                              sx={{
                                lineHeight: 1,
                                mt: 1,
                                py: 1,
                                position: 'relative',
                                zIndex: 2,
                              }}
                            >
                              {buttons[1].text}
                            </Box>
                          </Button>
                        </Link>
                      </Box>
                    </ButtonEntranceEffect>
                  )}
                </Flex>
              )}
            </Box>
          </Flex>
        </Container>
      </Flex>
    )
  }, [
    buttonJustifyContent,
    buttons,
    contentMaxWidth,
    contentMobilePosition,
    contentPadding,
    description,
    descriptionFontSize,
    fontSizes,
    justifyContent,
    preTitle,
    textAlign,
    textColor,
    title,
    titleFontSize,
    titleLineHeight,
    verticalPosition,
    additionalButtonText,
    additionalButtonUrl,
    forthButtonText,
    forthButtonUrl,
    titleTag,
  ])

  return (
    <>
      {/* Structured Data para SEO */}
      {structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      )}

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {/* Placeholder enquanto carrega */}
        {!shouldLoad && optimizedPoster && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${optimizedPoster})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              zIndex: 1,
            }}
          />
        )}

        {/* Vídeo com otimizações */}
        <video
          ref={videoRef}
          autoPlay={shouldLoad && autoplay}
          muted={muted}
          loop={loop}
          controls={controls}
          poster={optimizedPoster}
          preload={shouldLoad ? preload : 'none'}
          playsInline
          disablePictureInPicture={!controls}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            minHeight: minHeight?.[0] || '100vh',
          }}
          sx={{
            variant: 'styles.videoWithTextOverlayContainer',
            position: 'relative',
            minHeight,
            maxHeight: maxHeight ? [...maxHeight, 'none'] : 'none',
            aspectRatio: video
              ? (video.width || 16) / (video.height || 9)
              : 16 / 9,
            opacity: shouldLoad ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
          onLoadStart={() => {
            // Remove placeholder quando vídeo começa a carregar
            if (videoRef.current) {
              const placeholder =
                videoRef.current.parentElement?.querySelector(
                  '[data-placeholder]'
                )
              if (placeholder) {
                placeholder.remove()
              }
            }
          }}
          onError={() => {
            console.warn('Erro ao carregar vídeo:', video.url)
          }}
        >
          {/* Múltiplos formatos para melhor compatibilidade */}
          {shouldLoad && (
            <>
              {video.webmUrl && (
                <source src={video.webmUrl} type="video/webm" />
              )}
              <source src={video.url} type="video/mp4" />
            </>
          )}

          {/* Fallback para navegadores sem suporte */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: minHeight?.[0] || '100vh',
              backgroundColor: 'muted',
              color: 'text',
            }}
          >
            {optimizedPoster ? (
              <img
                src={optimizedPoster}
                alt={title || 'Video thumbnail'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Text>Seu navegador não suporta vídeos HTML5.</Text>
            )}
          </Box>
        </video>

        {/* Overlay com gradiente de opacidade */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'black',
            opacity: 0.15,
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'linear-gradient(to bottom, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.1) 20%, rgba(0, 0, 0, 0.0) 100%)',
            zIndex: 1,
          }}
        />

        {/* Camada de blur gradiente */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backdropFilter: 'blur(50px)',
              WebkitBackdropFilter: 'blur(50px)',
              mask: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 10%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0) 100%)',
              WebkitMask:
                'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 10%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0) 100%)',
            },
          }}
        />

        {maybeRenderContent}
      </Box>
    </>
  )
}

export default React.memo(VideoWithTextOverlay)
